"use client";

import React, { useEffect, useState } from "react";
import { Box, VStack, SlideFade } from "@chakra-ui/react";
import UserHeader from "./UserHeader";
import MetricsOverview from "./MetricsOverview";
import PerformanceMetrics from "./PerformanceMetrics";
import MeetingHistory from "./MeetingHistory";
import ActionItems from "./ActionItems";
import { SalesExecutiveData } from "./types";
import { salesPersonService } from "../../services/salesPersonService";
import { meetingService, Meeting as ApiMeeting, Pagination } from "../../services/meetingService";

const MEETINGS_PER_PAGE = 10;

interface SalesExecutiveProfileProps {
  executiveId?: any;
  executiveName?: string;
  executiveEmail?: string;
  onClose?: () => void;
  businessId: string;
  projectId?: string;
}

const SalesExecutiveProfile: React.FC<SalesExecutiveProfileProps> = ({ 
  executiveId = 1,
  executiveName,
  executiveEmail,
  onClose,
  businessId,
  projectId
}) => {
  const [executiveData, setExecutiveData] = useState<SalesExecutiveData | null>(null);
  const [showContent, setShowContent] = useState(false);
  const [details,setDetails] = useState<any>({});

  // Scoped meeting state — the list and its total count share one source.
  const [salesExecutiveId, setSalesExecutiveId] = useState<string | number | null>(null);
  const [meetings, setMeetings] = useState<ApiMeeting[]>([]);
  const [meetingPagination, setMeetingPagination] = useState<Pagination | null>(null);
  const [meetingsLoading, setMeetingsLoading] = useState(true);
  const [meetingsError, setMeetingsError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  // Fetch the executive's details once; capture the user_id the meetings API filters on.
  useEffect(() => {
    let cancelled = false;
    const fetchDetails = async () => {
      try {
        const response = await salesPersonService.getSalesPersonDetails(executiveId);
        if (cancelled) return;
        setDetails(response.stats);
        setSalesExecutiveId(response.details?.user_id ?? executiveId);
      } catch (error) {
        console.error('Error fetching details:', error);
        if (!cancelled) {
          setSalesExecutiveId(executiveId);
          setMeetingsError('Failed to load sales executive details.');
        }
      }
    }
    fetchDetails();

    // Small delay before starting content animations
    const timer = setTimeout(() => {
      setShowContent(true);
    }, 100);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [executiveId]);

  // Fetch this executive's meetings (server-side scoped + paginated) whenever the
  // resolved id or the page changes. pagination.totalItems is the single source of
  // truth for "Total Meetings".
  useEffect(() => {
    if (salesExecutiveId == null) return;
    let cancelled = false;
    const fetchMeetings = async () => {
      setMeetingsLoading(true);
      setMeetingsError(null);
      try {
        const response = await meetingService.getSalesPersonMeetings(salesExecutiveId, {
          page,
          limit: MEETINGS_PER_PAGE,
        });
        if (cancelled) return;
        setMeetings(response.data ?? []);
        setMeetingPagination(response.pagination ?? null);
      } catch (error) {
        console.error('Error fetching meetings:', error);
        if (!cancelled) setMeetingsError('Failed to load meeting history.');
      } finally {
        if (!cancelled) setMeetingsLoading(false);
      }
    };
    fetchMeetings();
    return () => { cancelled = true; };
  }, [salesExecutiveId, page]);

  // Function to calculate animation delay based on index
  const getAnimationDelay = (index: number) => {
    return 0.15 + (index * 0.08);
  };
  
  // Mock data fetch based on executiveId
  useEffect(() => {
    
    // In a real app, this would be an API call
    // Make sure the IDs match exactly what's in the SalesTeam component
    const mockData: Record<number, SalesExecutiveData> = {
      1: {
        name: "Rakesh S.",
        position: "Sales Executive",
        avatar: "https://cdn.builder.io/api/v1/image/assets/TEMP/d91706ac1d6e14463933b5d208c40ae34235e350?placeholderIfAbsent=true&apiKey=9181b4b5be8946e9a117f76f8f1082b9",
        email: "Rak123@gmail.com",
        phone: "+91-8543109785",
        department: "Sales",
        manager: "Vijay Kumar",
        joinDate: "Feb 2023"
      },
      2: {
        name: "Raj Arora",
        position: "Sales Executive",
        avatar: "https://cdn.builder.io/api/v1/image/assets/TEMP/d91706ac1d6e14463933b5d208c40ae34235e350?placeholderIfAbsent=true&apiKey=9181b4b5be8946e9a117f76f8f1082b9",
        email: "Rajarora238@gmail.com",
        phone: "+91-8543109785",
        department: "Sales",
        manager: "Priya Sharma",
        joinDate: "Apr 2023"
      },
      3: {
        name: "Aakash Gupta",
        position: "Senior Sales Executive",
        avatar: "https://cdn.builder.io/api/v1/image/assets/TEMP/d91706ac1d6e14463933b5d208c40ae34235e350?placeholderIfAbsent=true&apiKey=9181b4b5be8946e9a117f76f8f1082b9",
        email: "Aakash23Gupta@gmail.com",
        phone: "+91-8543109785",
        department: "Sales",
        manager: "Priya Sharma",
        joinDate: "Dec 2022"
      },
      4: {
        name: "Komal Singh",
        position: "Sales Executive",
        avatar: "https://cdn.builder.io/api/v1/image/assets/TEMP/d91706ac1d6e14463933b5d208c40ae34235e350?placeholderIfAbsent=true&apiKey=9181b4b5be8946e9a117f76f8f1082b9",
        email: "Komalsingh128@gmail.com",
        phone: "+91-8543109785",
        department: "Sales",
        manager: "Vijay Kumar",
        joinDate: "Mar 2023"
      }
    };
    
    // Log the executiveId being fetched
    
    // Check if we have data for this ID
    if (mockData[executiveId]) {      
      // If passed in name/email, override the mock data
      if (executiveName || executiveEmail) {
        const data = {...mockData[executiveId]};
        if (executiveName) data.name = executiveName;
        if (executiveEmail) data.email = executiveEmail;
        setExecutiveData(data);
      } else {
      setExecutiveData(mockData[executiveId]);
      }
    } else {      
      // Use the first one as default if not found
      if (executiveName || executiveEmail) {
        const data = {...mockData[1]};
        if (executiveName) data.name = executiveName;
        if (executiveEmail) data.email = executiveEmail;
        setExecutiveData(data);
      } else {
        setExecutiveData(mockData[1]); 
      }
    }
  }, [executiveId, executiveName, executiveEmail]);
  const email = (executiveEmail || executiveData?.email || "").toLowerCase();

  const displayPosition = (() => {
    switch (email) {
      case "simmy7069@gmail.com":
        return "Jr Sales Executive";

      case "info@rahejagroup.co.in":
        return "VP Sales & Marketing";

      case "kusum03041996@gmail.com":
        return "Sales Executive";
        
      case "sachingoswami077@gmail.com":
        return "AGM Sales";

      default:
        return executiveData?.position;
    }
  })();

  // Generic female avatar (Facebook-style gray silhouette) for specific executives;
  // everyone else keeps the default avatar.
  const FEMALE_AVATAR =
    "data:image/svg+xml;utf8," +
    encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="180" height="180" viewBox="0 0 200 200"><rect width="200" height="190" fill="#c8d0da"/><circle cx="100" cy="78" r="38" fill="#ffffff"/><path d="M40 190c0-33 27-58 60-58s60 25 60 58z" fill="#ffffff"/></svg>`
    );

  const displayAvatar = (() => {
    switch (email) {
      case "simmy7069@gmail.com":
      case "info@rahejagroup.co.in":
      case "kusum03041996@gmail.com":
        return FEMALE_AVATAR;

      default:
        return executiveData?.avatar ?? "";
    }
  })();

  return (
    <Box as="section" bg="gray.50" minH="100vh" pb={8}>
      <UserHeader 
        onClose={onClose} 
        executiveData={executiveData ? {
          name: executiveName || executiveData.name,
          position: displayPosition,
          avatar: displayAvatar,
          email: executiveEmail || executiveData.email,
          team: "Noida",
          id: executiveId || 1,
        } : undefined}
      />
      
      <Box pos="relative" mt={4} w="full">
        <VStack spacing={8} align="stretch" w="full" maxW="container.xl" mx="auto">
          <SlideFade 
            in={showContent} 
            offsetY="20px" 
            transition={{ enter: { duration: 0.5, delay: getAnimationDelay(0) } }}
          >
            <MetricsOverview
              details={{
                ...details,
                totalMeetings: meetingPagination?.totalItems ?? details.totalMeetings,
              }}
            />
          </SlideFade>
          
          {/* <SlideFade 
            in={showContent} 
            offsetY="20px" 
            transition={{ enter: { duration: 0.5, delay: getAnimationDelay(1) } }}
          >
            <PerformanceMetrics />
          </SlideFade> */}
          
          <SlideFade 
            in={showContent} 
            offsetY="20px" 
            transition={{ enter: { duration: 0.5, delay: getAnimationDelay(2) } }}
          >
            <MeetingHistory
              salesExecutiveName={executiveName || executiveData?.name}
              meetings={meetings}
              isLoading={meetingsLoading}
              error={meetingsError}
              pagination={meetingPagination}
              onPageChange={setPage}
            />
          </SlideFade>
          
          {/* <SlideFade 
            in={showContent} 
            offsetY="20px" 
            transition={{ enter: { duration: 0.5, delay: getAnimationDelay(3) } }}
          >
            <ActionItems />
          </SlideFade> */}
        </VStack>
      </Box>
    </Box>
  );
};

export default SalesExecutiveProfile; 